import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { GitCompareArrows, ArrowRight, Loader2, Info, UserPlus, UserMinus, RefreshCw, ShieldCheck } from "lucide-react";
import { authApi } from "../../lib/auth";
import { errorMessage } from "../../lib/api";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "");
const signed = (v) => (v > 0 ? `+${v.toLocaleString()}` : v < 0 ? `−${Math.abs(v).toLocaleString()}` : "0");
const tone = (v, badWhenUp = false) => (v === 0 || v === null || v === undefined ? "" : (v > 0) !== badWhenUp ? "ok" : "warn");

const COUNTS = [
  ["total", "Users", false],
  ["licensed", "Licensed users", false],
  ["unlicensed_members", "Unlicensed members", true],
  ["licensed_disabled", "Licensed but disabled", true],
  ["licensed_inactive", "Licensed but inactive", true],
  ["base_overlap", "Multiple base suites", true],
];
const SHOW = 100;

function UserList({ title, icon: Icon, items, total, render, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="ws-card glass" data-testid={testid}>
      <div className="ws-card-head">
        <h2>
          <Icon size={15} /> {title} <span className="count">{total.toLocaleString()}</span>
        </h2>
        {total > 0 && (
          <button type="button" className="tb-btn sm ghost" onClick={() => setOpen((o) => !o)} data-testid={`${testid}-toggle`}>
            {open ? "Hide" : "Show"}
          </button>
        )}
      </div>
      {total === 0 && <p className="muted">None.</p>}
      {open && (
        <div className="ws-table-wrap">
          <table className="ws-table">
            <tbody>
              {items.slice(0, SHOW).map((u) => (
                <tr key={u.upn}>
                  <td>
                    <div className="sku-name">{u.display_name}</div>
                    <div className="sku-part mono">{u.upn}</div>
                  </td>
                  <td>{u.department || "—"}</td>
                  <td>{render(u)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {total > SHOW && <p className="muted small">Showing the first {SHOW} of {total.toLocaleString()}.</p>}
        </div>
      )}
    </section>
  );
}

export default function ScanCompare({ tenantId, scans, currentScanId, initialFrom = "", initialTo = "" }) {
  const done = useMemo(() => scans.filter((s) => s.status === "done"), [scans]);
  const [to, setTo] = useState((initialTo && done.some((s) => s.id === initialTo) && initialTo) || currentScanId || done[0]?.id || "");
  const [from, setFrom] = useState((initialFrom && done.some((s) => s.id === initialFrom) && initialFrom) || "");
  const [diff, setDiff] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!from && done.length > 1) {
      const idx = done.findIndex((s) => s.id === to);
      setFrom((done[idx + 1] || done.find((s) => s.id !== to))?.id || "");
    }
  }, [done, to, from]);

  useEffect(() => {
    if (!from || !to || from === to) return undefined;
    let canceled = false;
    setLoading(true);
    authApi.tenants
      .compare(tenantId, from, to)
      .then((d) => !canceled && setDiff(d))
      .catch((err) => !canceled && toast.error("Could not compare scans", { description: errorMessage(err) }))
      .finally(() => !canceled && setLoading(false));
    return () => {
      canceled = true;
    };
  }, [tenantId, from, to]);

  if (done.length < 2) {
    return (
      <div className="ws-empty glass" data-testid="compare-empty">
        <GitCompareArrows size={24} />
        <h3>Need two completed scans</h3>
        <p>Run another scan later and come back here to see which seats, SKUs, users and policies changed.</p>
      </div>
    );
  }

  const changedSkus = diff?.skus.filter((s) => s.change !== "same") || [];
  return (
    <div className="ws-stack" data-testid="scan-compare">
      <div className="planner-bar glass compare-bar">
        <label className="ws-select">
          <span>From</span>
          <select value={from} onChange={(e) => setFrom(e.target.value)} data-testid="compare-from">
            {done.map((s) => (
              <option key={s.id} value={s.id} disabled={s.id === to}>
                {`${fmt(s.started_at)} · ${s.user_count} users`}
              </option>
            ))}
          </select>
        </label>
        <ArrowRight size={16} className="muted" />
        <label className="ws-select">
          <span>To</span>
          <select value={to} onChange={(e) => setTo(e.target.value)} data-testid="compare-to">
            {done.map((s) => (
              <option key={s.id} value={s.id} disabled={s.id === from}>
                {`${fmt(s.started_at)} · ${s.user_count} users`}
              </option>
            ))}
          </select>
        </label>
        {loading && <Loader2 size={15} className="spin muted" />}
      </div>

      {diff && (
        <>
          <div className="kpi-grid compare" data-testid="compare-counts">
            {COUNTS.map(([key, label, badUp]) => {
              const c = diff.counts[key];
              return (
                <div key={key} className={`kpi glass ${tone(c.delta, badUp)}`} data-testid={`compare-count-${key}`}>
                  <div>
                    <div className={`kpi-value mono ${tone(c.delta, badUp)}`}>{signed(c.delta)}</div>
                    <div className="kpi-label">{label}</div>
                    <div className="kpi-sub mono">
                      {c.before.toLocaleString()} → {c.after.toLocaleString()}
                    </div>
                  </div>
                </div>
              );
            })}
            <div className={`kpi glass ${tone(diff.gaps.delta, true)}`} data-testid="compare-count-gaps">
              <div>
                <div className={`kpi-value mono ${tone(diff.gaps.delta, true)}`}>{diff.gaps.delta === null ? "n/a" : signed(diff.gaps.delta)}</div>
                <div className="kpi-label">Users with license gaps</div>
                <div className="kpi-sub mono">
                  {diff.gaps.before ?? "n/a"} → {diff.gaps.after ?? "n/a"}
                </div>
              </div>
            </div>
          </div>

          <section className="ws-card glass" data-testid="compare-skus">
            <div className="ws-card-head">
              <h2>
                License changes <span className="count">{changedSkus.length}</span>
              </h2>
              <span className="muted small">
                {fmt(diff.from.started_at)} → {fmt(diff.to.started_at)}
              </span>
            </div>
            {changedSkus.length === 0 && <p className="muted">No SKU or seat changes between these scans.</p>}
            {changedSkus.length > 0 && (
              <div className="ws-table-wrap">
                <table className="ws-table">
                  <thead>
                    <tr>
                      <th>SKU</th>
                      <th>Change</th>
                      <th className="num">Purchased</th>
                      <th className="num">Assigned</th>
                      <th className="num">Unassigned</th>
                    </tr>
                  </thead>
                  <tbody>
                    {changedSkus.map((s) => (
                      <tr key={s.part_number} data-testid={`compare-sku-${s.part_number}`}>
                        <td>
                          <div className="sku-name">{s.name}</div>
                          <div className="sku-part mono">{s.part_number}</div>
                        </td>
                        <td>
                          <span className={`mini-chip ${s.change === "added" ? "ok" : s.change === "removed" ? "warn" : ""}`}>{s.change}</span>
                        </td>
                        {["enabled", "consumed", "unassigned"].map((f) => (
                          <td key={f} className="num mono">
                            {s.before ? s.before[f].toLocaleString() : "—"} → {s.after ? s.after[f].toLocaleString() : "—"}{" "}
                            <span className={tone(s.delta[f] || 0, f === "unassigned")}>({signed(s.delta[f] || 0)})</span>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <div className="compare-users">
            <UserList title="Users added" icon={UserPlus} items={diff.users.added} total={diff.users.counts.added} testid="compare-users-added" render={(u) => <span className="muted small">{u.licenses.join(", ") || "no license"}</span>} />
            <UserList title="Users removed" icon={UserMinus} items={diff.users.removed} total={diff.users.counts.removed} testid="compare-users-removed" render={(u) => <span className="muted small">{u.licenses.join(", ") || "no license"}</span>} />
            <UserList
              title="License or status changes"
              icon={RefreshCw}
              items={diff.users.changed}
              total={diff.users.changed.length}
              testid="compare-users-changed"
              render={(u) => (
                <div className="lic-chips">
                  {u.gained.map((l) => (
                    <span key={`g${l}`} className="mini-chip ok">
                      + {l}
                    </span>
                  ))}
                  {u.lost.map((l) => (
                    <span key={`l${l}`} className="mini-chip warn">
                      − {l}
                    </span>
                  ))}
                  {u.enabled_before !== u.enabled_after && <span className="mini-chip">{u.enabled_after ? "re-enabled" : "disabled"}</span>}
                </div>
              )}
            />
          </div>

          <section className="ws-card glass" data-testid="compare-ca">
            <h2>
              <ShieldCheck size={15} /> Conditional Access changes
            </h2>
            {!diff.ca.available && (
              <div className="ws-alert">
                <Info size={13} /> Policies were not read in one of the scans.
              </div>
            )}
            {diff.ca.available && diff.ca.added.length + diff.ca.removed.length + diff.ca.changed.length === 0 && <p className="muted">No policy changes.</p>}
            <ul className="ca-diff">
              {diff.ca.added.map((p) => (
                <li key={p.id}>
                  <span className="mini-chip ok">added</span> {p.name} <span className="muted small">({p.state})</span>
                </li>
              ))}
              {diff.ca.removed.map((p) => (
                <li key={p.id}>
                  <span className="mini-chip warn">removed</span> {p.name}
                </li>
              ))}
              {diff.ca.changed.map((p) => (
                <li key={p.id}>
                  <span className="mini-chip">changed</span> {p.name} <span className="muted small">{p.fields.join(", ")}{p.state_before !== p.state_after ? ` · ${p.state_before} → ${p.state_after}` : ""}</span>
                </li>
              ))}
            </ul>
          </section>
        </>
      )}
    </div>
  );
}
